import React, { FC, ReactNode, useMemo, useRef, useState } from "react";

import {
  modalCancel,
  modalInput,
  modalPrimary,
  PortalModal,
} from "../../clientPortal/PortalModal";
import {
  SignatureFieldView,
  SignatureRequestView,
  SignPayload,
} from "../signature.types";
import {
  FIELD_LABELS,
  fieldsLeft,
  localDateLabel,
  signedNoticeText,
  signerLine,
} from "../signature.utils";
import { AdoptedSignature, AdoptSignatureModal } from "./AdoptSignatureModal";
import { PdfPages } from "./PdfPages";

const primary =
  "h-10 rounded-[9px] bg-[#6B4EFF] px-5 text-[13.5px] font-extrabold text-white shadow-[0_3px_10px_rgba(107,78,255,0.3)] hover:bg-[#5B3DF5] disabled:cursor-not-allowed disabled:opacity-60";
const secondary =
  "h-10 rounded-[9px] border-[1.5px] border-[#DCE2EA] bg-white px-4 text-[13.5px] font-bold text-[#3A4256] hover:bg-[#F7F8FA]";

const ShieldIcon = () => (
  <svg
    width={"14"}
    height={"14"}
    viewBox={"0 0 24 24"}
    fill={"none"}
    stroke={"currentColor"}
    strokeWidth={"2.2"}
    aria-hidden={"true"}>
    <path d={"M12 3 4 6v6c0 5 3.4 8.4 8 9 4.6-.6 8-4 8-9V6l-8-3Z"} />
    <path d={"m9 12 2 2 4-4"} />
  </svg>
);

const hexA = (hex: string, alpha: number) => {
  const n = parseInt(hex.replace("#", ""), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
};

/** A centred message instead of the document (not your turn, already signed…). */
export const SigningNotice: FC<{
  title: string;
  text?: ReactNode;
  children?: ReactNode;
}> = ({ title, text, children }) => (
  <div
    className={
      "mx-auto my-8 flex w-[calc(100%-32px)] max-w-[520px] flex-col items-center gap-3 rounded-2xl border border-[#E8E8EC] bg-white px-6 py-10 text-center"
    }>
    <h1 className={"m-0 text-xl font-extrabold text-[#1F2433]"}>{title}</h1>
    {text && (
      <div className={"text-sm font-medium leading-relaxed text-[#6B7185]"}>
        {text}
      </div>
    )}
    {children}
  </div>
);

/** The signer list shown after signing / on finished requests. */
export const SignerList: FC<{ view: SignatureRequestView }> = ({ view }) => (
  <ul className={"m-0 flex w-full list-none flex-col gap-2 p-0 text-left"}>
    {view.signers.map((s, i) => (
      <li
        key={s.id}
        className={
          "flex items-center gap-2.5 rounded-[10px] border border-[#EDEFF3] px-3 py-2.5 text-[13px] font-semibold text-[#3A4256]"
        }>
        <span
          className={"h-2.5 w-2.5 shrink-0 rounded-full"}
          style={{ background: s.color }}
        />
        <span className={"flex-1"}>
          {signerLine(s, view.inOrder, view.signers[i - 1]?.name)}
        </span>
        {s.status === "signed" && <span className={"text-[#12805C]"}>✓</span>}
      </li>
    ))}
  </ul>
);

/**
 * Signing screen shared by clients (portal) and firm users (countersigning):
 * the document with this signer's fields, adopt-signature window, decline, finish.
 */
export const SigningView: FC<{
  view: SignatureRequestView;
  loadPdf: () => Promise<ArrayBuffer>;
  identityNote: string;
  defaultName: string;
  onSign: (body: SignPayload) => Promise<string | null>;
  onDecline: (reason: string) => Promise<string | null>;
  /** Shown once signed or when there is nothing to do. */
  doneActions?: ReactNode;
  onPdfError?: (error: Error) => void;
}> = ({
  view,
  loadPdf,
  identityNote,
  defaultName,
  onSign,
  onDecline,
  doneActions,
  onPdfError,
}) => {
  const me = view.signers.find((s) => s.isMe);
  const mine = useMemo(
    () => view.fields.filter((f) => f.signerId === me?.id),
    [view.fields, me?.id],
  );
  const [adopted, setAdopted] = useState<AdoptedSignature | null>(null);
  const [placed, setPlaced] = useState<Record<string, boolean>>({});
  const [values, setValues] = useState<Record<string, string>>({});
  const [adoptFor, setAdoptFor] = useState<string | null>(null);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [declined, setDeclined] = useState(false);
  const fieldEls = useRef<Record<string, HTMLElement | null>>({});
  const today = localDateLabel();
  const signedName = adopted?.signedName ?? defaultName;

  const isDone = (f: SignatureFieldView) => {
    if (f.type === "signature" || f.type === "initials") return !!placed[f.id];
    if (f.type === "text") return !!values[f.id]?.trim();
    if (f.type === "checkbox") return values[f.id] === "true";
    return true; // date and name fill themselves
  };
  const { left, total } = fieldsLeft(mine, isDone);
  const needsInitials = mine.some((f) => f.type === "initials");

  const goNext = () => {
    const next =
      mine.find((f) => f.required && !isDone(f)) ??
      mine.find((f) => !isDone(f));
    const el = next && fieldEls.current[next.id];
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
    if (el instanceof HTMLInputElement) el.focus({ preventScroll: true });
  };

  const clickSignField = (f: SignatureFieldView) => {
    if (placed[f.id]) return setPlaced((p) => ({ ...p, [f.id]: false }));
    if (!adopted) return setAdoptFor(f.id);
    setPlaced((p) => ({ ...p, [f.id]: true }));
  };

  const finish = async () => {
    setError(null);
    if (left > 0) {
      goNext();
      return setError(
        `Please complete the ${
          left === 1 ? "last field" : `${left} remaining fields`
        } first.`,
      );
    }
    if (
      !adopted &&
      mine.some((f) => f.type === "signature" || f.type === "initials")
    )
      return setAdoptFor("");
    setBusy(true);
    const problem = await onSign({
      signatureImage: adopted?.signatureImage,
      initialsImage: adopted?.initialsImage,
      signedName,
      localDate: today,
      agree: true,
      values: mine
        .filter((f) => f.type === "text" || f.type === "checkbox")
        .map((f) => ({
          fieldId: f.id,
          value: values[f.id] ?? (f.type === "checkbox" ? "false" : ""),
        })),
    });
    setBusy(false);
    if (problem) setError(problem);
  };

  const decline = async () => {
    setBusy(true);
    const problem = await onDecline(reason.trim());
    setBusy(false);
    if (problem) return setError(problem);
    setDeclineOpen(false);
    setDeclined(true);
  };

  if (declined)
    return (
      <SigningNotice
        title={"You declined to sign"}
        text={`We let ${view.firmName} know${
          reason.trim() ? ", with your reason" : ""
        }. They may contact you about it.`}>
        {doneActions}
      </SigningNotice>
    );

  if (me?.status === "signed" || view.status === "completed")
    return (
      <SigningNotice
        title={
          view.status === "completed"
            ? "Everyone has signed"
            : "Thank you, you've signed"
        }
        text={signedNoticeText(view)}>
        <SignerList view={view} />
        {doneActions}
      </SigningNotice>
    );

  if (!view.canSign) {
    const text =
      view.status === "cancelled"
        ? `${view.firmName} cancelled this signature request.`
        : view.status === "expired"
        ? "This signature request has expired. Ask the firm to send it again if you still need to sign."
        : view.status === "declined"
        ? `This request was ${view.statusText.toLowerCase()}.`
        : view.waitingFor
        ? `${view.waitingFor} signs first. We'll email you when it's your turn.`
        : "This document isn't waiting for your signature.";
    return (
      <SigningNotice title={view.documentName} text={text}>
        {doneActions}
      </SigningNotice>
    );
  }

  const renderField = (f: SignatureFieldView) => {
    const signer = view.signers.find((s) => s.id === f.signerId);
    const color = signer?.color ?? "#6B4EFF";
    const style = {
      left: `${f.x * 100}%`,
      top: `${f.y * 100}%`,
      width: `${f.width * 100}%`,
      height: `${f.height * 100}%`,
    };
    if (f.signerId !== me?.id) {
      // Someone else's field: their filled text, or a faint box with their name.
      return (
        <div
          key={f.id}
          className={
            "absolute flex items-center overflow-hidden rounded-[4px] px-1 text-[10px] font-semibold"
          }
          style={{
            ...style,
            background: f.value ? "transparent" : hexA(color, 0.1),
            color: f.value ? "#1F2433" : color,
          }}>
          <span className={"truncate"}>
            {f.value === "true"
              ? "☑"
              : f.value || `${signer?.name ?? ""} · ${FIELD_LABELS[f.type]}`}
          </span>
        </div>
      );
    }
    const done = isDone(f);
    const box = `absolute flex items-center overflow-hidden rounded-[4px] border-[1.5px] ${
      done ? "border-transparent" : "border-dashed"
    }`;
    const boxStyle = {
      ...style,
      borderColor: done ? "transparent" : color,
      background: done ? "transparent" : hexA(color, 0.14),
    };
    if (f.type === "signature" || f.type === "initials") {
      const img =
        f.type === "signature"
          ? adopted?.signatureImage
          : adopted?.initialsImage;
      return (
        <button
          key={f.id}
          type={"button"}
          ref={(el) => (fieldEls.current[f.id] = el)}
          onClick={() => clickSignField(f)}
          className={`${box} justify-center`}
          style={boxStyle}
          title={done ? "Click to remove" : undefined}>
          {done && img ? (
            <img
              src={img}
              alt={f.type}
              className={"h-full max-w-full object-contain"}
            />
          ) : (
            <span
              className={"truncate text-[11px] font-extrabold"}
              style={{ color }}>
              {f.type === "signature" ? "Click to sign" : "Initials"}
            </span>
          )}
        </button>
      );
    }
    if (f.type === "checkbox")
      return (
        <label
          key={f.id}
          className={`${box} cursor-pointer justify-center`}
          style={{ ...boxStyle, borderStyle: "solid" }}
          title={f.label ?? "Checkbox"}>
          <input
            type={"checkbox"}
            ref={(el) => (fieldEls.current[f.id] = el)}
            aria-label={f.label ?? "Checkbox"}
            checked={values[f.id] === "true"}
            onChange={(e) =>
              setValues((v) => ({
                ...v,
                [f.id]: e.target.checked ? "true" : "false",
              }))
            }
            className={"h-[80%] w-[80%] accent-[#6B4EFF]"}
          />
        </label>
      );
    if (f.type === "text")
      return (
        <input
          key={f.id}
          ref={(el) => (fieldEls.current[f.id] = el)}
          aria-label={f.label ?? "Text"}
          placeholder={f.label ?? "Text"}
          maxLength={300}
          value={values[f.id] ?? ""}
          onChange={(e) => setValues((v) => ({ ...v, [f.id]: e.target.value }))}
          className={`${box} bg-transparent px-1 text-[12px] font-semibold text-[#1F2433] outline-none`}
          style={{
            ...boxStyle,
            borderStyle: values[f.id] ? "solid" : "dashed",
            borderColor: color,
          }}
        />
      );
    // date / name: filled automatically
    return (
      <div
        key={f.id}
        className={`${box} px-1 text-[12px] font-semibold text-[#1F2433]`}
        style={{ ...boxStyle, background: hexA(color, 0.06) }}>
        <span className={"truncate"}>
          {f.type === "date" ? today : signedName}
        </span>
      </div>
    );
  };

  return (
    <div className={"flex min-h-0 flex-1 flex-col"}>
      <div className={"sticky top-0 z-20 border-b border-[#E8E8EC] bg-white"}>
        <div
          className={
            "mx-auto flex max-w-[1100px] flex-wrap items-center gap-3 px-4 py-3"
          }>
          <div className={"flex min-w-0 flex-1 flex-col"}>
            <span
              className={"truncate text-[15px] font-extrabold text-[#1F2433]"}>
              {view.documentName}
            </span>
            <span
              className={
                "flex items-center gap-1 text-[12px] font-semibold text-[#12805C]"
              }>
              <ShieldIcon />
              {identityNote}
            </span>
          </div>
          <button
            type={"button"}
            className={secondary}
            onClick={() => setDeclineOpen(true)}>
            Decline
          </button>
          <button
            type={"button"}
            className={primary}
            onClick={finish}
            disabled={busy}>
            {busy ? "Signing…" : "Finish"}
          </button>
        </div>
        <div className={"border-t border-[#EDEFF3] bg-[#F7F5FF]"}>
          <div
            className={
              "mx-auto flex max-w-[1100px] flex-wrap items-center gap-3 px-4 py-2 text-[13px] font-semibold text-[#3A4256]"
            }>
            <span className={"flex-1"}>
              {left > 0 ? (
                <>
                  <strong>{left}</strong> of {total} field
                  {total === 1 ? "" : "s"} left
                  {view.inOrder &&
                    view.signers.find(
                      (s) => s.order === (me?.order ?? 0) + 1,
                    ) && (
                      <span className={"text-[#6B7185]"}>
                        {" "}
                        ·{" "}
                        {
                          view.signers.find(
                            (s) => s.order === (me?.order ?? 0) + 1,
                          )!.name
                        }{" "}
                        signs after you
                      </span>
                    )}
                </>
              ) : (
                <>All fields done. Click Finish to sign.</>
              )}
            </span>
            {left > 0 && (
              <button
                type={"button"}
                onClick={goNext}
                className={
                  "h-8 rounded-[8px] bg-[#6B4EFF] px-3 text-[12.5px] font-extrabold text-white"
                }>
                Next field ↓
              </button>
            )}
          </div>
        </div>
        {error && (
          <div
            className={
              "border-t border-[#F3C5C5] bg-[#FDF1F1] px-4 py-2 text-center text-[13px] font-semibold text-[#B42318]"
            }>
            {error}
          </div>
        )}
      </div>

      {view.message && (
        <div
          className={
            "mx-auto mt-4 w-full max-w-[760px] rounded-[10px] border border-[#EDEFF3] bg-white px-4 py-3 text-[13px] text-[#3A4256]"
          }>
          <strong>Message from {view.sentBy || view.firmName}:</strong>{" "}
          {view.message}
        </div>
      )}

      <div className={"px-3 py-5"}>
        <PdfPages
          load={loadPdf}
          loadKey={view.id}
          onError={onPdfError}
          overlay={(page) =>
            view.fields.filter((f) => f.page === page).map(renderField)
          }
        />
      </div>

      {adoptFor !== null && (
        <AdoptSignatureModal
          defaultName={defaultName}
          firmName={view.firmName}
          needsInitials={needsInitials}
          onClose={() => setAdoptFor(null)}
          onAdopt={(a) => {
            setAdopted(a);
            if (adoptFor) setPlaced((p) => ({ ...p, [adoptFor]: true }));
            setAdoptFor(null);
          }}
        />
      )}

      {declineOpen && (
        <PortalModal
          title={"Decline to sign?"}
          subtitle={`${view.firmName} will be told. Nobody else will be able to sign this request.`}
          onClose={() => setDeclineOpen(false)}
          width={480}
          footer={
            <>
              <button
                type={"button"}
                className={modalCancel}
                onClick={() => setDeclineOpen(false)}>
                Keep signing
              </button>
              <button
                type={"button"}
                className={`${modalPrimary} !bg-[#D92D20]`}
                onClick={decline}
                disabled={busy}>
                Decline
              </button>
            </>
          }>
          <label
            className={
              "flex flex-col gap-1.5 text-[13px] font-bold text-[#3A4256]"
            }>
            Reason (optional)
            <textarea
              className={`${modalInput} h-24 py-2`}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={"e.g. The amount on page 2 is wrong"}
            />
          </label>
        </PortalModal>
      )}
    </div>
  );
};
